<script setup lang="ts">
/**
 * Final Dividends — 문서 패키지 (Stage 스테퍼 + 주주구성 시나리오).
 *   Stage 1 Directors' Resolution · Stage 2 Notice / Cover Letter ·
 *   Stage 3 Minutes / Representative Minutes / Notification · Stage 4 Dividend Voucher(주주 1인당 1부).
 *   시나리오(Multiple / Sole Individual / Sole Corporate)로 문서 세트가 갈리고, Voucher 는 멤버 수만큼 반복.
 *   멤버 add/remove·Voucher add/remove 수동 가능. v-show 로 단계 전환(입력 보존).
 */
import { computed, provide, ref, watch } from 'vue';
import type { Component } from 'vue';
import DocScope from './cdd/DocScope.vue';
import DrDoc from './docs/DrDoc.vue';
import NoticeEgmDoc from './docs/NoticeEgmDoc.vue';
import NoticeWrittenDoc from './docs/NoticeWrittenDoc.vue';
import MinutesMultipleDoc from './docs/MinutesMultipleDoc.vue';
import MinutesSoleIndivDoc from './docs/MinutesSoleIndivDoc.vue';
import RepMinutesDoc from './docs/RepMinutesDoc.vue';
import VoucherDoc from './docs/VoucherDoc.vue';
import CoverLetterDoc from './docs/CoverLetterDoc.vue';
import NotificationDoc from './docs/NotificationDoc.vue';
import { DIV_SCENARIO, SCENARIO_LABEL, type DivScenario } from './docs/scenario';
import { DIV_MEMBERS } from './docs/members';

type Slot =
  | { kind: 'fixed'; icon: string; name: string; comp: Component; key: string }
  | { kind: 'repeat'; icon: string; name: string; comp: Component; scope: string; id: number };

interface StageDef { no: number; en: string; short: string; canAddMember?: boolean; buildSlots: () => Slot[]; }

const props = defineProps<{
  autoLocal?: { scenario?: DivScenario; memberIds?: number[]; memberSummary?: string[] } | null;
}>();

// ── 시나리오 ──
const scenario = ref<DivScenario>('multiple');
provide(DIV_SCENARIO, scenario);
const SCENARIOS: DivScenario[] = ['multiple', 'sole_individual', 'sole_corporate'];

// ── 확인 게이트: 회사 선택 → 식별 결과(주주구성) 확인 → [이대로 진행] → 문서 화면 ──
const confirmed = ref(false);
const detectedScenario = ref<DivScenario>('multiple');
const memberSummary = ref<string[]>([]);
function proceedFromGate() { confirmed.value = true; }
function reopenGate() { confirmed.value = false; }

// ── 멤버(주주) 목록 — Voucher 반복 + Minutes 표 공유 ──
const memberIds = ref<number[]>([1]);
let memberSeq = 1;
function addMember() { memberSeq += 1; memberIds.value.push(memberSeq); }
function removeMember(id: number) {
  memberIds.value = memberIds.value.filter((x) => x !== id);
  if (!memberIds.value.length) { memberIds.value = [1]; memberSeq = 1; }
}
provide(DIV_MEMBERS, { ids: memberIds, add: addMember, remove: removeMember });

watch(() => props.autoLocal, (v) => {
  if (!v) return;
  if (v.scenario && SCENARIOS.includes(v.scenario)) { scenario.value = v.scenario; detectedScenario.value = v.scenario; }
  if (Array.isArray(v.memberIds) && v.memberIds.length) {
    memberIds.value = [...v.memberIds];
    memberSeq = Math.max(...v.memberIds);
  }
  if (Array.isArray(v.memberSummary)) memberSummary.value = [...v.memberSummary];
  // 새 회사 식별 결과가 오면 다시 확인받는다(문서 화면 전 게이트).
  confirmed.value = false;
}, { deep: true, immediate: true });

// Sole 시나리오면 멤버 1명으로 축소(솔멤버는 1인).
watch(scenario, (s) => {
  if (s !== 'multiple' && memberIds.value.length > 1) { memberIds.value = [memberIds.value[0]]; }
});

const activeStages = computed((): StageDef[] => {
  const sc = scenario.value;
  const stages: Omit<StageDef, 'no'>[] = [
    {
      en: "Directors' Resolution", short: 'DR',
      buildSlots: () => [{ kind: 'fixed', key: 'dr', icon: '📝', name: "Directors' Resolutions In Writing (Final Dividend)", comp: DrDoc }],
    },
    {
      en: sc === 'sole_corporate' ? 'Notice & Cover Letter' : 'Notice of EGM', short: 'Notice',
      buildSlots: () => (sc === 'sole_corporate'
        ? [
            { kind: 'fixed' as const, key: 'notice_w', icon: '📨', name: 'Notice — Final Dividends (by written means)', comp: NoticeWrittenDoc },
            { kind: 'fixed' as const, key: 'cover', icon: '✉️', name: 'Cover Letter to Member', comp: CoverLetterDoc },
          ]
        : [{ kind: 'fixed' as const, key: 'notice_egm', icon: '📨', name: 'Notice of Extraordinary General Meeting', comp: NoticeEgmDoc }]),
    },
    {
      en: sc === 'sole_corporate' ? 'Representative Minutes & Notification' : 'Minutes of EGM', short: 'Minutes',
      buildSlots: () => {
        if (sc === 'sole_corporate') {
          return [
            { kind: 'fixed' as const, key: 'rep_minutes', icon: '📋', name: 'Representative Minutes (s.179(6))', comp: RepMinutesDoc },
            { kind: 'fixed' as const, key: 'notification', icon: '📢', name: 'Notification of Resolution Passed', comp: NotificationDoc },
          ];
        }
        if (sc === 'sole_individual') {
          return [{ kind: 'fixed' as const, key: 'minutes_si', icon: '📋', name: 'Minutes of EGM (s.184G)', comp: MinutesSoleIndivDoc }];
        }
        return [{ kind: 'fixed' as const, key: 'minutes_m', icon: '📋', name: 'Minutes of Extraordinary General Meeting', comp: MinutesMultipleDoc }];
      },
    },
    {
      en: 'Dividend Voucher', short: 'Voucher',
      canAddMember: true,
      buildSlots: () => memberIds.value.map((id) => ({
        kind: 'repeat' as const, icon: '🧾', name: 'Dividend Voucher', comp: VoucherDoc, scope: `mem_${id}/`, id,
      })),
    },
  ];
  return stages.map((st, i) => ({ ...st, no: i + 1 }));
});

const currentStage = ref(1);
const totalStages = computed(() => activeStages.value.length);
function go(n: number) { if (n >= 1 && n <= totalStages.value) currentStage.value = n; }
watch(totalStages, (n) => { if (currentStage.value > n) currentStage.value = Math.max(1, n); });

const view = computed(() => {
  let n = 0;
  return activeStages.value.map((st) => ({
    no: st.no, en: st.en, short: st.short, canAddMember: !!st.canAddMember,
    docs: st.buildSlots().map((d) => ({ ...d, no: (n += 1) })),
  }));
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));

/* zoom */
const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }

function gatherLocalState() {
  return { scenario: scenario.value, memberIds: [...memberIds.value], currentStage: currentStage.value, confirmed: confirmed.value };
}
function restoreLocalState(s: { scenario?: DivScenario; memberIds?: number[]; currentStage?: number; confirmed?: boolean } | null) {
  if (!s) return;
  if (s.scenario && SCENARIOS.includes(s.scenario)) scenario.value = s.scenario;
  if (Array.isArray(s.memberIds) && s.memberIds.length) { memberIds.value = [...s.memberIds]; memberSeq = Math.max(...s.memberIds); }
  if (s.currentStage && s.currentStage >= 1 && s.currentStage <= totalStages.value) currentStage.value = s.currentStage;
  // 저장된 task 재진입 시엔 이미 확인된 것으로 간주(게이트 skip).
  confirmed.value = s.confirmed !== undefined ? !!s.confirmed : true;
}
defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="fd-pkg">
    <!-- 확인 게이트: 회사 선택 후 식별된 주주구성을 확인받고 나서 문서 화면을 연다 -->
    <div v-if="!confirmed" class="fd-gate">
      <div class="gate-icon">🔎</div>
      <div class="gate-title">Member Structure Summary</div>
      <p class="gate-lead">Based on the records currently on file, this company's member structure is as follows.</p>
      <div class="gate-result">
        <span class="gate-badge">{{ SCENARIO_LABEL[detectedScenario] }}</span>
        <span v-if="memberSummary.length" class="gate-count">{{ memberSummary.length }} member{{ memberSummary.length > 1 ? 's' : '' }}</span>
      </div>
      <ul v-if="memberSummary.length" class="gate-members">
        <li v-for="(m, i) in memberSummary" :key="i">{{ m }}</li>
      </ul>
      <p v-else class="gate-empty">No member records on file. Choose the member structure below, or select the company again.</p>
      <p class="gate-q">Proceed with this?</p>
      <div class="gate-actions">
        <button type="button" class="gate-proceed" @click="proceedFromGate">Proceed →</button>
        <label class="gate-override">
          If the member structure differs, change:
          <select v-model="scenario" class="scenario-sel">
            <option v-for="s in SCENARIOS" :key="s" :value="s">{{ SCENARIO_LABEL[s] }}</option>
          </select>
        </label>
      </div>
    </div>

    <template v-else>
    <div class="confirmed-head">
      <span class="ch-lbl">Member structure</span>
      <span class="ch-badge">{{ SCENARIO_LABEL[scenario] }}</span>
      <button type="button" class="ch-reopen" @click="reopenGate">Review again</button>
    </div>

    <div class="stepper">
      <button v-for="st in view" :key="st.no" type="button" class="step" :class="{ active: currentStage === st.no, done: currentStage > st.no }" @click="go(st.no)">
        <span class="step-no">{{ currentStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.short }}</small></span>
      </button>
    </div>

    <div class="zoom-bar">
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>

    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage {{ st.no }}</span><span class="stage-t">{{ st.en }}</span></div>
        <div v-if="st.canAddMember" class="fd-toolbar">
          <span class="fd-toolbar-lbl">Dividend Voucher</span>
          <button type="button" class="fd-tool" @click="addMember()">+ Add member (voucher)</button>
        </div>
        <div v-for="d in st.docs" :key="d.kind === 'fixed' ? d.key : d.scope" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">{{ d.icon }}</span><span class="nm">{{ d.name }}</span>
            <template v-if="d.kind === 'repeat'">
              <span class="copy">member #{{ d.id }}</span>
              <button v-if="memberIds.length > 1" type="button" class="fd-rm" title="Remove voucher" @click="removeMember(d.id)">×</button>
            </template>
            <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
          </div>
          <div class="simple-frame">
            <DocScope v-if="d.kind === 'repeat'" :scope="d.scope"><component :is="d.comp" /></DocScope>
            <component v-else :is="d.comp" />
          </div>
        </div>
      </section>
    </div>

    <div class="stage-nav">
      <button v-if="currentStage > 1" type="button" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ totalStages }}</span>
      <button v-if="currentStage < totalStages" type="button" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
    </template>
  </div>
</template>

<style scoped>
.fd-pkg { color: #111; }
.scenario-sel { border: 1px solid #b9d0ff; border-radius: 8px; padding: 6px 10px; font-size: 13px; font-weight: 700; color: #1f2d4d; background: #fff; }
/* 확인 게이트 */
.fd-gate { width: 560px; max-width: 100%; margin: 24px auto; padding: 28px 28px 24px; background: #fff; border: 1px solid #e6ebf3; border-radius: 16px; box-shadow: 0 8px 28px rgba(20,30,60,0.08); text-align: center; box-sizing: border-box; }
.gate-icon { font-size: 30px; }
.gate-title { font-size: 17px; font-weight: 800; color: #1f2d4d; margin: 8px 0 4px; }
.gate-lead { font-size: 13px; color: #5b6b82; margin: 0 0 16px; }
.gate-result { display: flex; align-items: center; justify-content: center; gap: 10px; margin: 0 0 12px; }
.gate-badge { background: #1b64da; color: #fff; font-weight: 800; font-size: 14px; padding: 7px 16px; border-radius: 999px; }
.gate-count { font-size: 12.5px; font-weight: 700; color: #42506a; }
.gate-members { list-style: none; margin: 0 auto 14px; padding: 12px 16px; max-width: 420px; text-align: left; background: #f7f9fc; border: 1px solid #e6ebf3; border-radius: 10px; }
.gate-members li { font-size: 12.5px; color: #2c3a54; padding: 3px 0; border-bottom: 1px dashed #e2e8f2; }
.gate-members li:last-child { border-bottom: none; }
.gate-empty { font-size: 12.5px; color: #b06a00; background: #fff7e8; border: 1px solid #f0dcae; border-radius: 10px; padding: 10px 14px; margin: 0 0 14px; }
.gate-q { font-size: 14px; font-weight: 800; color: #1f2d4d; margin: 6px 0 16px; }
.gate-actions { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.gate-proceed { border: none; background: #1b64da; color: #fff; font-size: 14px; font-weight: 800; border-radius: 10px; padding: 12px 28px; cursor: pointer; }
.gate-proceed:hover { background: #164fb0; }
.gate-override { font-size: 12px; color: #6b7a94; display: inline-flex; align-items: center; gap: 8px; }
/* 확인 후 헤더 */
.confirmed-head { display: flex; align-items: center; gap: 10px; width: 794px; max-width: 100%; margin: 0 auto 10px; padding: 8px 14px; background: #eafaf1; border: 1px solid #bfe6cf; border-radius: 10px; box-sizing: border-box; }
.ch-lbl { font-size: 11.5px; font-weight: 800; color: #42506a; }
.ch-badge { font-size: 12.5px; font-weight: 800; color: #14713f; background: #d6f2e1; padding: 3px 12px; border-radius: 999px; }
.ch-reopen { margin-left: auto; border: 1px solid #9cc7ad; background: #fff; color: #2b7a4b; font-size: 11.5px; font-weight: 700; border-radius: 8px; padding: 5px 12px; cursor: pointer; }
.ch-reopen:hover { background: #f0faf3; }
.stepper { display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20,30,60,0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; }
.step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; }
.doc-zoom-btn { min-width: 36px; font-size: 15px; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.stage { width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; }
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }
.fd-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.fd-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; }
.fd-tool { border: 1px solid #1b84ff; background: #fff; color: #1b84ff; border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer; }
.fd-tool:hover { background: #eaf3ff; }
.pkg-doc { width: 100%; }
.pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; }
.pkg-doclabel .ic { font-size: 14px; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.fd-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 16px; width: 794px; max-width: 100%; margin: 36px auto 12px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn:disabled { opacity: 0.55; cursor: default; }
.nav-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
</style>
